/**
 * XLSX file parsing for import uploads.
 *
 * Parses the first sheet of an XLSX workbook into the same
 * { headers, rows } shape produced by parseImportCsv, so the
 * existing upload pipeline works without API changes.
 *
 * Dates are normalized to ISO 8601 (YYYY-MM-DD) strings.
 * Numbers are preserved as numeric strings for the backend normalizer.
 */
import * as XLSX from 'xlsx';

export interface ParsedImportSheet {
  headers: string[];
  rows: Record<string, unknown>[];
  sheetName: string;
  totalRows: number;
}

function cellToString(cell: XLSX.CellObject | undefined): unknown {
  if (!cell) return '';
  if (cell.t === 'd' && cell.v instanceof Date) {
    const d = cell.v;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  if (cell.t === 'n') return cell.v;
  if (cell.t === 'b') return cell.v;
  return cell.w ?? cell.v ?? '';
}

export async function parseImportXlsx(file: File): Promise<ParsedImportSheet> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true, cellText: false });

  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('XLSX_NO_SHEETS');
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error('XLSX_EMPTY_SHEET');

  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  if (range.e.r < 1) {
    return { headers: [], rows: [], sheetName, totalRows: 0 };
  }

  const headers: string[] = [];
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddr = XLSX.utils.encode_cell({ r: range.s.r, c: col });
    const cell = ws[cellAddr];
    const text = String(cell?.w ?? cell?.v ?? '').trim();
    headers.push(text);
  }

  const rows: Record<string, unknown>[] = [];
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const record: Record<string, unknown> = {};
    let hasValue = false;
    for (let col = range.s.c; col <= range.e.c; col++) {
      const header = headers[col - range.s.c];
      if (!header) continue;
      const cellAddr = XLSX.utils.encode_cell({ r: row, c: col });
      const value = cellToString(ws[cellAddr]);
      record[header] = value;
      if (value !== '' && value !== null && value !== undefined) hasValue = true;
    }
    if (hasValue) rows.push(record);
  }

  return { headers, rows, sheetName, totalRows: rows.length };
}

export function isXlsxFile(file: File): boolean {
  return (
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}
